import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, FileText, Receipt, Users, LogOut, Shield, Building, Settings, Package, ShoppingCart, Truck, ClipboardList, Archive, AlertTriangle, Menu, X, UserCog, CalendarCheck, Calendar, Repeat } from 'lucide-react';
import InstallPrompt from '@/components/InstallPrompt';

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    navigate('/admin/login');
  };

  const userRole = localStorage.getItem('role') || 'admin';

  const menuCategories = [
    {
      label: 'Overview',
      items: [
        { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' }
      ]
    },
    {
      label: 'Sales & Operations',
      items: [
        { path: '/admin/clients', icon: Building, label: 'Clients' },
        { path: '/admin/quotes', icon: FileText, label: 'Quotes' },
        { path: '/admin/invoices', icon: Receipt, label: 'Invoices' },
        { path: '/admin/contacts', icon: Users, label: 'Contacts' }
      ]
    },
    {
      label: 'Service Management',
      items: [
        { path: '/admin/assets', icon: Archive, label: 'Asset Register' },
        { path: '/admin/service-reports', icon: ClipboardList, label: 'Service Reports' },
        { path: '/admin/defects', icon: AlertTriangle, label: 'Defects' }
      ]
    },
    {
      label: 'Task Management',
      items: [
        { path: '/admin/tasks', icon: CalendarCheck, label: 'Tasks' },
        { path: '/admin/schedule', icon: Calendar, label: 'Schedule' },
        { path: '/admin/recurring-tasks', icon: Repeat, label: 'Recurring Tasks' },
        { path: '/admin/technicians', icon: UserCog, label: 'Technicians' }
      ]
    },
    {
      label: 'Inventory',
      items: [
        { path: '/admin/suppliers', icon: Truck, label: 'Suppliers' },
        { path: '/admin/products', icon: Package, label: 'Products' },
        { path: '/admin/purchase-orders', icon: ShoppingCart, label: 'Purchase Orders' }
      ]
    },
    ...((['managing_director', 'administration_manager'].includes(userRole)) ? [{
      label: 'Administration',
      items: [
        { path: '/admin/users', icon: Shield, label: 'Admin Users' },
        { path: '/admin/settings', icon: Settings, label: 'Settings' },
        { path: '/admin/roles', icon: Shield, label: 'Roles' }
      ]
    }] : [])
  ];

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-950 via-black to-gray-950 admin-panel">
      {/* Header with Burger Menu */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-xl border-b border-white/10 p-4">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
          <img 
            src="https://customer-assets.emergentagent.com/job_safefire/artifacts/vcg3q2eg_path1000.png" 
            alt="Mertek Logo" 
            className="h-8 w-auto"
          />
        </div>
      </div>

      {/* Sidebar - Always Toggleable */}
      <aside className={`
        fixed inset-0 z-40 w-64 bg-black/95 backdrop-blur-xl border-r border-white/10 p-6 flex flex-col
        transition-transform duration-300
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        mt-16
      `}>
        <div className="mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_safefire/artifacts/vcg3q2eg_path1000.png" 
            alt="Mertek Logo" 
            className="h-9 w-auto mb-2"
          />
          <p className="text-gray-300 text-sm font-medium">Admin Panel</p>
        </div>

        <nav className="space-y-6 flex-1 overflow-y-auto pr-2">
          {menuCategories.map((category, catIndex) => (
            <div key={catIndex} className="space-y-2">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 mb-2">
                {category.label}
              </h3>
              {category.items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <button
                    key={item.path}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    onClick={() => {
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
                      isActive
                        ? 'bg-gradient-to-r from-orange-500/20 to-red-500/20 text-orange-400 border border-orange-500/30'
                        : 'text-gray-300 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="mt-auto pt-6">
          <Button
            data-testid="logout-btn"
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/5"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Overlay for menu */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto mt-16">
        <div className="max-w-7xl mx-auto">
          {children}
        </div>
      </main>

      {/* Install Prompt for PWA */}
      <InstallPrompt />
    </div>
  );
};

export default AdminLayout;